// AuthProvider — context global del estado de auth.
//
// Maneja:
//   - Hidratación inicial de la sesión desde localStorage
//   - Suscripción a cambios de auth (signin/signout/refresh)
//   - Parse de claims del JWT activo
//   - Acciones: signInWithPassword, signOut, switchTenant
//
// `switchTenant` actualiza `user_metadata.active_tenant_id` y fuerza un
// refresh del JWT. GoTrue invoca el `custom_access_token_hook` (mig 00012)
// que re-genera los claims con el tenant nuevo.

import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import type { JwtClaims } from 'schemas';
import { supabase } from '../supabase';
import { parseClaimsFromToken } from './parseClaims';

/** Estado discriminado del auth — la UI hace narrowing exhaustivo sobre `kind`. */
export type AuthState =
  | { kind: 'loading' }
  | { kind: 'signed_out' }
  | { kind: 'signed_in'; session: Session; claims: JwtClaims }
  | { kind: 'incomplete_claims'; session: Session };

export interface AuthContextValue {
  state: AuthState;
  signInWithPassword: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  /** Cambia el tenant activo (caso típico: super_admin pasando entre clientes). */
  switchTenant: (tenantId: string) => Promise<{ error: Error | null }>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  // undefined = cargando, null = signed_out, Session = signed_in (con o sin claims completos)
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setSession(data.session);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event: AuthChangeEvent, s) => {
      if (mounted) setSession(s);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const state: AuthState = useMemo(() => {
    if (session === undefined) return { kind: 'loading' };
    if (session === null) return { kind: 'signed_out' };
    const claims = parseClaimsFromToken(session.access_token);
    if (!claims) return { kind: 'incomplete_claims', session };
    return { kind: 'signed_in', session, claims };
  }, [session]);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const switchTenant = useCallback(async (tenantId: string) => {
    // 1. Actualizar user_metadata (el hook custom_access_token_hook lee de acá)
    const { error: updateError } = await supabase.auth.updateUser({
      data: { active_tenant_id: tenantId },
    });
    if (updateError) return { error: updateError };

    // 2. Forzar refresh del access token para que el hook re-corra y
    //    regenere claims con el nuevo tenant_id_activo
    const { error: refreshError } = await supabase.auth.refreshSession();
    return { error: refreshError };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ state, signInWithPassword, signOut, switchTenant }),
    [state, signInWithPassword, signOut, switchTenant],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
